import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { motion } from 'framer-motion';
import { 
  FolderPlus, 
  Upload, 
  Folder, 
  File, 
  Download, 
  Trash2,
  ArrowLeft,
  FileText,
  FileImage,
  FileArchive,
  FileSpreadsheet,
  MoreVertical,
  Search,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API = '/api';

function getFileIcon(contentType) {
  if (contentType?.startsWith('image/')) return FileImage;
  if (contentType?.includes('pdf')) return FileText;
  if (contentType?.includes('spreadsheet') || contentType?.includes('excel')) return FileSpreadsheet;
  if (contentType?.includes('zip') || contentType?.includes('archive')) return FileArchive;
  return File;
}

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function DocumentsPage() {
  const { isAdmin } = useAuth();
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [editingFolder, setEditingFolder] = useState(null);
  
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  // Debounced search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await axios.get(`${API}/files/search`, {
          params: { q: searchQuery },
          withCredentials: true,
        });
        setSearchResults(data);
      } catch {
        toast.error('Vyhledávání se nezdařilo');
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchData = useCallback(async () => {
    try {
      const [foldersRes, filesRes] = await Promise.all([
        axios.get(`${API}/folders`, { withCredentials: true }),
        axios.get(`${API}/files${currentFolder ? `?folder_id=${currentFolder.id}` : ''}`, { withCredentials: true })
      ]);
      setFolders(foldersRes.data.filter(f => f.parent_id === (currentFolder?.id || null)));
      setFiles(filesRes.data.filter(f => f.folder_id === (currentFolder?.id || null)));
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Nepodařilo se načíst data');
    } finally {
      setLoading(false);
    }
  }, [currentFolder]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error('Zadejte název složky');
      return;
    }

    try {
      if (editingFolder) {
        await axios.put(`${API}/folders/${editingFolder.id}`, { name: newFolderName }, { withCredentials: true });
        toast.success('Složka byla upravena');
      } else {
        await axios.post(`${API}/folders`, { name: newFolderName, parent_id: currentFolder?.id || null }, { withCredentials: true });
        toast.success('Složka byla vytvořena');
      }
      setFolderDialogOpen(false);
      setNewFolderName('');
      setEditingFolder(null);
      fetchData();
    } catch (error) {
      toast.error('Operace se nezdařila');
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm('Opravdu chcete smazat tuto složku a všechny soubory v ní?')) return;
    
    try {
      await axios.delete(`${API}/folders/${folderId}`, { withCredentials: true });
      toast.success('Složka byla smazána');
      fetchData();
    } catch (error) {
      toast.error('Nepodařilo se smazat složku');
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    if (currentFolder) {
      formData.append('folder_id', currentFolder.id);
    }

    try {
      await axios.post(`${API}/files/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Soubor byl nahrán');
      fetchData();
    } catch (error) {
      toast.error('Nepodařilo se nahrát soubor');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleDownloadFile = async (file) => {
    try {
      const response = await axios.get(`${API}/files/${file.id}/download`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Nepodařilo se stáhnout soubor');
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Opravdu chcete smazat tento soubor?')) return;
    
    try {
      await axios.delete(`${API}/files/${fileId}`, { withCredentials: true });
      toast.success('Soubor byl smazán');
      fetchData();
    } catch (error) {
      toast.error('Nepodařilo se smazat soubor');
    }
  };

  const openEditFolder = (folder) => {
    setEditingFolder(folder);
    setNewFolderName(folder.name);
    setFolderDialogOpen(true);
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="documents-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            {currentFolder && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentFolder(null)}
                data-testid="back-to-root"
                className="text-[#57534E] hover:text-[#16A34A]"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Zpět
              </Button>
            )}
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-[#052E16] font-['Manrope']">
                {currentFolder ? currentFolder.name : 'Dokumenty'}
              </h1>
              <p className="text-[#57534E] mt-1">
                Správa firemních dokumentů
              </p>
            </div>
          </div>
          
          {isAdmin && (
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setEditingFolder(null);
                  setNewFolderName('');
                  setFolderDialogOpen(true);
                }}
                data-testid="create-folder-btn"
                className="border-[#E2E8F0] text-[#1C1917] hover:bg-[#F0FDF4] hover:border-[#16A34A]"
              >
                <FolderPlus className="w-4 h-4 mr-2" />
                Nová složka
              </Button>
              
              <label className="cursor-pointer">
                <input
                  type="file"
                  className="hidden"
                  onChange={handleFileUpload}
                  disabled={uploading}
                  data-testid="file-upload-input"
                />
                <Button
                  asChild
                  disabled={uploading}
                  data-testid="upload-file-btn"
                  className="bg-[#16A34A] hover:bg-[#15803D] text-white font-semibold rounded-xl shadow-sm"
                >
                  <span>
                    <Upload className="w-4 h-4 mr-2" />
                    {uploading ? 'Nahrávám...' : 'Nahrát soubor'}
                  </span>
                </Button>
              </label>
            </div>
          )}
        </div>

        {/* Search Bar */}
        <div className="relative" data-testid="documents-search">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#57534E]" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Hledat v dokumentech..."
            data-testid="documents-search-input"
            className="pl-10 pr-10 bg-white border-[#E2E8F0] text-[#1C1917] focus:border-[#16A34A] rounded-xl"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#57534E] hover:text-[#1C1917]"
              data-testid="documents-search-clear"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Search Results */}
        {searchResults !== null ? (
          <div className="space-y-4">
            <h2 className="text-sm font-bold uppercase tracking-wider text-[#57534E]">
              {searching ? 'Hledám...' : `Nalezeno: ${searchResults.length} ${searchResults.length === 1 ? 'soubor' : searchResults.length < 5 ? 'soubory' : 'souborů'}`}
            </h2>
            {searchResults.length > 0 ? (
              <Card className="bg-white border-[#E2E8F0] shadow-sm">
                <CardContent className="p-0">
                  <div className="divide-y divide-[#E2E8F0]" data-testid="search-results-list">
                    {searchResults.map((file, index) => {
                      const FileIcon = getFileIcon(file.content_type);
                      return (
                        <motion.div
                          key={file.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.03 }}
                          className="flex items-center justify-between p-4 hover:bg-[#F0FDF4] transition-colors"
                          data-testid={`search-result-${file.id}`}
                        >
                          <div className="flex items-center gap-4 min-w-0">
                            <div className="w-10 h-10 rounded-xl bg-[#F0FDF4] flex items-center justify-center flex-shrink-0">
                              <FileIcon className="w-5 h-5 text-[#16A34A]" strokeWidth={1.5} />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-[#1C1917] truncate">
                                {file.original_filename}
                              </p>
                              <p className="text-sm text-[#57534E]">
                                {formatFileSize(file.size)} • {new Date(file.created_at).toLocaleDateString('cs-CZ')}
                                {file.folder_name && (
                                  <span className="ml-2 px-1.5 py-0.5 bg-[#F0FDF4] text-[#16A34A] rounded text-xs font-medium">
                                    {file.folder_name}
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownloadFile(file)}
                              data-testid={`search-download-${file.id}`}
                              className="text-[#57534E] hover:text-[#16A34A]"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            {isAdmin && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteFile(file.id)}
                                data-testid={`search-delete-${file.id}`}
                                className="text-[#57534E] hover:text-red-500"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ) : !searching && (
              <Card className="bg-white border-[#E2E8F0]">
                <CardContent className="py-12 text-center">
                  <Search className="w-12 h-12 text-[#E2E8F0] mx-auto mb-3" />
                  <p className="text-[#57534E]">Žádné dokumenty neodpovídají vyhledávání</p>
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
        <>
        {/* Content */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 rounded-2xl bg-[#F0FDF4] animate-pulse" />
            ))}
          </div>
        ) : (
          <>
            {/* Folders */}
            {folders.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-sm font-bold uppercase tracking-wider text-[#57534E]">
                  Složky
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="folders-grid">
                  {folders.map((folder, index) => (
                    <motion.div
                      key={folder.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Card 
                        className="bg-white border-[#E2E8F0] hover:border-[#16A34A]/40 hover:shadow-md transition-all cursor-pointer group relative"
                        onClick={() => setCurrentFolder(folder)}
                        data-testid={`folder-${folder.id}`}
                      >
                        <CardContent className="p-6 flex flex-col items-center justify-center text-center">
                          <Folder className="w-12 h-12 text-[#F59E0B] mb-3 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
                          <span className="font-medium text-[#1C1917] group-hover:text-[#16A34A] transition-colors line-clamp-2">
                            {folder.name}
                          </span>
                        </CardContent>
                        
                        {isAdmin && (
                          <div className="absolute top-2 right-2" onClick={(e) => e.stopPropagation()}>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#57534E] hover:text-[#1C1917]">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent className="bg-white border-[#E2E8F0]">
                                <DropdownMenuItem 
                                  onClick={() => openEditFolder(folder)}
                                  className="text-[#1C1917] focus:bg-[#F0FDF4] focus:text-[#16A34A]"
                                >
                                  Přejmenovat
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleDeleteFolder(folder.id)}
                                  className="text-red-600 focus:bg-[#FEE2E2] focus:text-red-600"
                                >
                                  Smazat
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        )}
                      </Card>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Files */}
            {files.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-sm font-bold uppercase tracking-wider text-[#57534E]">
                  Soubory
                </h2>
                <Card className="bg-white border-[#E2E8F0] shadow-sm">
                  <CardContent className="p-0">
                    <div className="divide-y divide-[#E2E8F0]" data-testid="files-list">
                      {files.map((file, index) => {
                        const FileIcon = getFileIcon(file.content_type);
                        return (
                          <motion.div
                            key={file.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="flex items-center justify-between p-4 hover:bg-[#F0FDF4] transition-colors group"
                            data-testid={`file-${file.id}`}
                          >
                            <div className="flex items-center gap-4 min-w-0">
                              <div className="w-10 h-10 rounded-xl bg-[#F0FDF4] flex items-center justify-center flex-shrink-0">
                                <FileIcon className="w-5 h-5 text-[#16A34A]" strokeWidth={1.5} />
                              </div>
                              <div className="min-w-0">
                                <p className="font-medium text-[#1C1917] truncate">
                                  {file.original_filename}
                                </p>
                                <p className="text-sm text-[#57534E]">
                                  {formatFileSize(file.size)} • {new Date(file.created_at).toLocaleDateString('cs-CZ')}
                                </p>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownloadFile(file)}
                                data-testid={`download-file-${file.id}`}
                                className="text-[#57534E] hover:text-[#16A34A]"
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                              
                              {isAdmin && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteFile(file.id)}
                                  data-testid={`delete-file-${file.id}`}
                                  className="text-[#57534E] hover:text-red-500"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Empty State */}
            {folders.length === 0 && files.length === 0 && (
              <Card className="bg-white border-[#E2E8F0]">
                <CardContent className="py-16 text-center">
                  <Folder className="w-16 h-16 text-[#E2E8F0] mx-auto mb-4" strokeWidth={1} />
                  <p className="text-[#57534E]">
                    {currentFolder ? 'Tato složka je prázdná' : 'Zatím zde nejsou žádné dokumenty'}
                  </p>
                  {isAdmin && (
                    <p className="text-sm text-[#57534E] mt-2">
                      Vytvořte složku nebo nahrajte soubor
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}
        </>
        )}

        {/* Folder Dialog */}
        <Dialog open={folderDialogOpen} onOpenChange={setFolderDialogOpen}>
          <DialogContent className="bg-white border-[#E2E8F0] text-[#1C1917]">
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">
                {editingFolder ? 'Přejmenovat složku' : 'Nová složka'}
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <Label>Název složky</Label>
              <Input
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Zadejte název..."
                data-testid="folder-name-input"
                className="mt-2 bg-white border-[#E2E8F0] text-[#1C1917] focus:border-[#16A34A]"
              />
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setFolderDialogOpen(false)}
                className="border-[#E2E8F0] text-[#57534E] hover:bg-[#F8FAF8]"
              >
                Zrušit
              </Button>
              <Button
                onClick={handleCreateFolder}
                data-testid="submit-folder-btn"
                className="bg-[#16A34A] hover:bg-[#15803D] text-white font-semibold"
              >
                {editingFolder ? 'Uložit' : 'Vytvořit'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
